import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import { useGlobalModal } from "../Contexts/GlobalModalContext";

// TODO: Use this instead of buttons in other pages
interface DeleteButtonProps {
  /** Async delete handler; should return true on success */
  handleDelete: () => Promise<boolean>;
  /** Optional button label, defaults to "Delete" */
  buttonLabel?: string;
  /** Confirmation message, defaults to generic */
  confirmMessage?: string;
  /** If false, skips confirmation modal and deletes immediately */
  requireConfirmation?: boolean;
  disabled?: boolean
}

export const DeleteButton: React.FC<DeleteButtonProps> = ({
  handleDelete,
  buttonLabel = 'Delete',
  confirmMessage = 'Are you sure you want to delete this item? This action cannot be undone.',
  requireConfirmation = true,
  disabled = false
}) => {
  const { showModal, hideModal } = useGlobalModal();
  const [isWorking, setIsWorking] = useState(false);

  const DeleteModalContent: React.FC = () => {
    const [isModalWorking, setIsModalWorking] = useState(false);

    const onConfirm = async () => {
      setIsWorking(true);
      setIsModalWorking(true);
      const success = await handleDelete();
      setIsWorking(false);
      setIsModalWorking(false);
      if (success) hideModal();
    };

    return (
      <form>
        <div>
          <h2 className="border-b border-theme-border font-semibold pb-2 mb-8 text-[1.6rem] text-theme-text">
            <span>Confirm Deletion</span>
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              className={`text-theme-text text-sm transition ease-out mb-2 ml-1 opacity-${isModalWorking ? 100 : 0}`} />
          </h2>
          <p className="!text-theme-text">{confirmMessage}</p>
        </div>
        <div className=" flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={hideModal}
            className="px-4 py-2 bg-theme-surface hover:bg-theme-surface-2 text-theme-text transition rounded-sm cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 bg-theme-danger text-white hover:bg-theme-danger-hover transition rounded-sm cursor-pointer"
          >
            Delete
          </button>
        </div>
      </form>
    );
  }

  const handleClick = async () => {
    if (requireConfirmation) {
      showModal({ content: <DeleteModalContent /> });
    } else {
      setIsWorking(true);
      await handleDelete();
      setIsWorking(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isWorking || disabled}
      className="relative px-4 py-2 bg-theme-danger hover:bg-theme-danger-hover text-white font-bold rounded-sm transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          className={`text-theme-text text-sm transition ease-out opacity-${isWorking ? 100 : 0}`} />
      </span>
      <span className={`${isWorking ? 'opacity-0' : 'opacity-100'} transition ease-out`}>{buttonLabel}</span>
    </button>
  );
};

export default DeleteButton;
